import type { ChatbotAction, ChatbotMessage } from '../../types/chatbot-message.types';

import { ActionButtonGroup } from './action-button-group';
import { AssistantEmptyMessage } from './messages/assistant-empty-message';
import { AssistantErrorMessage } from './messages/assistant-error-message';
import { AssistantTextMessage } from './messages/assistant-text-message';
import { ComparisonCard } from './messages/comparison-card';
import { DayDividerPill } from './messages/day-divider-pill';
import { KpiBullet } from './messages/kpi-bullet';
import { LabeledCard } from './messages/labeled-card';
import { NarrativeMessage } from './messages/narrative-message';
import { NextStepCard } from './messages/next-step-card';
import { RecommendationCard } from './messages/recommendation-card';
import { StatusCard } from './messages/status-card';
import { UserMessage } from './messages/user-message';

interface ChatbotMessageRendererProps {
  message: ChatbotMessage;
  onAction?: ((id: ChatbotAction['id']) => void) | undefined;
  /** Retry handler for a failed Q&A turn — receives the errored message's id so each card retries its own turn. */
  onRetry?: ((messageId: string) => void) | undefined;
}

/** Single dispatch point — switches on `message.kind` and renders the right component. */
export function ChatbotMessageRenderer({
  message,
  onAction,
  onRetry,
}: ChatbotMessageRendererProps) {
  switch (message.kind) {
    case 'day-divider':
      return <DayDividerPill day={message.day} label={message.label} />;
    case 'kpi-bullet':
      return <KpiBullet label={message.label} primary={message.primary} lift={message.lift} />;
    case 'status':
      return (
        <StatusCard
          tone={message.tone}
          badge={message.badge}
          title={message.title}
          body={message.body}
          metrics={message.metrics}
        />
      );
    case 'labeled-card':
      return <LabeledCard title={message.title} body={message.body} metrics={message.metrics} />;
    case 'comparison':
      return (
        <ComparisonCard
          tone={message.tone}
          badge={message.badge}
          columns={message.columns}
          rows={message.rows}
        />
      );
    case 'narrative':
      return <NarrativeMessage text={message.text} />;
    case 'next-step':
      return <NextStepCard eyebrow={message.eyebrow} body={message.body} />;
    case 'recommendation':
      return (
        <RecommendationCard
          tone={message.tone}
          badge={message.badge}
          body={message.body}
          subBody={message.subBody}
          metrics={message.metrics}
          actions={message.actions}
          onAction={onAction}
        />
      );
    case 'actions':
      return <ActionButtonGroup actions={message.actions} onAction={onAction} />;
    case 'inline-icon-actions':
      // TODO: restore InlineIconActionsRow once the backend agent exposes real tool-call data.
      return null;
    case 'assistant-text':
      return <AssistantTextMessage text={message.text} streaming={message.streaming} />;
    case 'assistant-error':
      return (
        <AssistantErrorMessage
          message={message.message}
          retryable={message.retryable}
          onRetry={
            onRetry
              ? () => {
                  onRetry(message.id);
                }
              : undefined
          }
        />
      );
    case 'assistant-empty':
      return (
        <AssistantEmptyMessage
          onRetry={
            onRetry
              ? () => {
                  onRetry(message.id);
                }
              : undefined
          }
        />
      );
    case 'user':
      return <UserMessage text={message.text} />;
  }
}
